import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, FileClock } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useAppSelector } from '@/app/hooks';
import { useFormat } from '@/hooks/useAccess';
import { useStockLedgerQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime, formatMoney, formatQuantity } from '@/lib/format';
import {
  Badge, Card, EmptyState, ErrorState, Input, LoadingState, PageHeader, Pagination,
  Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';

const MOVEMENT_TYPES = [
  'OPENING', 'PURCHASE', 'SALE', 'CUSTOMER_RETURN', 'SUPPLIER_RETURN', 'ADJUSTMENT',
  'TRANSFER_OUT', 'TRANSFER_IN', 'DAMAGE', 'LOSS', 'EXPIRY', 'PRODUCTION_OUTPUT',
  'PRODUCTION_CONSUMPTION', 'COUNT_ADJUSTMENT',
];

const TONE_BY_TYPE: Record<string, 'success' | 'danger' | 'warning' | 'info' | 'neutral'> = {
  OPENING: 'info',
  PURCHASE: 'success',
  SALE: 'info',
  CUSTOMER_RETURN: 'success',
  SUPPLIER_RETURN: 'warning',
  ADJUSTMENT: 'neutral',
  TRANSFER_IN: 'success',
  TRANSFER_OUT: 'warning',
  DAMAGE: 'danger',
  LOSS: 'danger',
  EXPIRY: 'danger',
  COUNT_ADJUSTMENT: 'warning',
};

/**
 * The append-only history behind every stock figure. Entries are never edited —
 * a correction is a new, opposing entry — so this list is the audit trail.
 */
export function StockLedgerPage() {
  const format = useFormat();
  const warehouses = useAppSelector((state) => state.tenant.context?.warehouses ?? []);

  const [page, setPage] = useState(1);
  const [warehouseId, setWarehouseId] = useState('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading, isError, error, refetch } = useStockLedgerQuery({
    page,
    limit: 30,
    warehouseId: warehouseId || undefined,
    type: type || undefined,
    from: from || undefined,
    to: to || undefined,
  });

  return (
    <>
      <PageHeader
        title="Stock ledger"
        description="Every movement ever posted, with the balance before and after. Entries are immutable."
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="w-full sm:w-52">
            <Select label="Warehouse" value={warehouseId} onChange={(event) => { setWarehouseId(event.target.value); setPage(1); }}>
              <option value="">All warehouses</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-52">
            <Select label="Movement type" value={type} onChange={(event) => { setType(event.target.value); setPage(1); }}>
              <option value="">All types</option>
              {MOVEMENT_TYPES.map((movement) => (
                <option key={movement} value={movement}>
                  {movement.toLowerCase().replaceAll('_', ' ')}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-44">
            <Input label="From" type="date" value={from} onChange={(event) => { setFrom(event.target.value); setPage(1); }} />
          </div>
          <div className="w-full sm:w-44">
            <Input label="To" type="date" value={to} onChange={(event) => { setTo(event.target.value); setPage(1); }} />
          </div>
        </div>

        {isLoading ? (
          <LoadingState label="Loading the ledger…" />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={FileClock}
            title="No movements recorded"
            description="Purchases, sales, transfers and adjustments all appear here as soon as they are posted."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Product</Th>
                  <Th>Type</Th>
                  <Th>Warehouse</Th>
                  <Th numeric>Quantity</Th>
                  <Th numeric>Balance after</Th>
                  <Can permission={P.PRODUCT_COST_VIEW}>
                    <Th numeric>Unit cost</Th>
                  </Can>
                  <Th>Reference</Th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((entry) => (
                  <tr key={entry.id} className="hover:bg-sunken">
                    <Td className="whitespace-nowrap text-xs text-secondary">
                      {formatDateTime(entry.occurredAt, format)}
                    </Td>
                    <Td>
                      <p className="font-medium">{entry.variant.product.name}</p>
                      <p className="text-xs text-muted">{entry.variant.sku}</p>
                    </Td>
                    <Td>
                      <Badge tone={TONE_BY_TYPE[entry.type] ?? 'neutral'}>
                        {entry.type.toLowerCase().replaceAll('_', ' ')}
                      </Badge>
                    </Td>
                    <Td className="text-secondary">{entry.warehouse.name}</Td>
                    <Td numeric>
                      <span
                        className={
                          entry.direction === 'IN'
                            ? 'inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400'
                            : 'inline-flex items-center gap-1 font-medium text-red-600 dark:text-red-400'
                        }
                      >
                        {entry.direction === 'IN' ? (
                          <ArrowDownLeft className="size-3.5" aria-hidden />
                        ) : (
                          <ArrowUpRight className="size-3.5" aria-hidden />
                        )}
                        {formatQuantity(entry.quantity, format)}
                      </span>
                      <span className="ml-1 text-xs text-muted">{entry.unit.symbol}</span>
                    </Td>
                    <Td numeric className="font-medium">{formatQuantity(entry.balanceAfter, format)}</Td>
                    <Can permission={P.PRODUCT_COST_VIEW}>
                      <Td numeric className="text-secondary">{formatMoney(entry.unitCost, format)}</Td>
                    </Can>
                    <Td className="text-xs text-muted">
                      {entry.referenceNumber ?? entry.referenceType.toLowerCase().replaceAll('_', ' ')}
                      {entry.notes && <p className="mt-0.5">{entry.notes}</p>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>
    </>
  );
}
